import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Notification,
  NotificationListParams,
  NotificationPreferences,
  UpdatePreferencesInput,
} from '@/types';

export function useNotifications(params?: NotificationListParams) {
  return useQuery({
    queryKey: ['notifications', 'list', params],
    queryFn: () =>
      api.get<Notification[]>('/notifications', {
        page: params?.page ? String(params.page) : undefined,
        per_page: params?.per_page ? String(params.per_page) : undefined,
        is_read: params?.is_read !== undefined ? String(params.is_read) : undefined,
        notification_type: params?.notification_type,
        search: params?.search || undefined,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    select: (res) => res.data.count,
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch('/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => api.get<NotificationPreferences>('/notifications/preferences'),
    select: (res) => res.data,
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePreferencesInput) => api.patch('/notifications/preferences', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });
}
